/* eslint-disable react-hooks/exhaustive-deps */
// TODO: rewrite this hook to avoid any exhaustive-deps issues

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
  CancelTokenStatic
} from 'axios'
import { useState, useEffect, useRef } from 'react'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import SupportAPI from 'SupportAPI'
import { MaybeArray } from 'utils/types'

const CancelToken: CancelTokenStatic = axios.CancelToken

export type UseAgentError = AxiosError | Error | string | boolean

type UseAgentRequestEntry = string | AxiosRequestConfig
type UseAgentRequestConfig = MaybeArray<UseAgentRequestEntry>

type ResponseData = Record<string, unknown> | unknown[] | string

type ConfigList = AxiosRequestConfig[]

const cache: Record<string, ResponseData> = {
  /**
   * [cacheKey]: data: {}|[]|string,
   */
}

type OpenRequests<ResponseType> = Record<string, Promise<ResponseType>>

export type MaybeArrayValue<ValType, RefType> = RefType extends any[]
  ? ValType[]
  : ValType

const openRequests: OpenRequests<ResponseType> = {
  /**
   * [cacheKey]: Promise
   */
}

const createCacheKey = (options) => {
  return JSON.stringify(options)
}

export const getHasError = (...errors: MaybeArray<UseAgentError>[]): boolean =>
  errors.flat().some((maybeError) => Boolean(maybeError))

export const getIsLoading = (
  ...loadingStates: MaybeArray<boolean>[]
): boolean => loadingStates.flat().some(Boolean)

interface UseAgentOptions {
  shouldCache?: boolean
  skipRequest?: boolean
  interval?: number
  ignoreStatusCode?: number
  lazy?: boolean
}

interface UseAgentState<DataType> {
  success: MaybeArrayValue<boolean, DataType>
  loading: MaybeArrayValue<boolean, DataType>
  data: DataType | undefined
  status: MaybeArrayValue<number, DataType>
  error: MaybeArrayValue<UseAgentError, DataType>
  isLoading: boolean
  hasError: boolean
  fetchSuccessCount: number
  cancelTokenSource: MaybeArrayValue<CancelTokenSource, DataType>
  doFetch(): void
}

export function useSupportAgent<ResponseDataType>(
  requestConfig: UseAgentRequestConfig,
  initialData: ResponseDataType,
  options: UseAgentOptions = {}
): UseAgentState<ResponseDataType> {
  return useAgent(requestConfig, initialData, options, SupportAPI)
}

export default function useAgent<ResponseDataType>(
  requestConfig: UseAgentRequestConfig,
  initialData: ResponseDataType,
  options: UseAgentOptions = {},
  apiAgent: AxiosInstance = agent
): UseAgentState<ResponseDataType> {
  // track whether the component is mounted so that we can avoid attempting state updates in async callbacks
  // (this intentionally uses a ref instead of state to avoid triggering a re-render)
  const isMounted = useRef(true)
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const intervalRef = useRef(null)
  const {
    lazy = false,
    shouldCache = true,
    interval = false,
    skipRequest,
    ignoreStatusCode
  } = options
  const { authHeader, currentTeamId } = useAuth()
  const isMultiReq = Array.isArray(requestConfig)
  const cacheKeys = []

  const reqList: UseAgentRequestConfig = Array.isArray(requestConfig)
    ? requestConfig
    : [requestConfig]

  const configList: ConfigList = reqList
    .filter((c) => Boolean(c))
    .map((config) => {
      let finalOptions: {
        url?: string
        headers?: { [key: string]: string }
      } = {}

      if (typeof config === 'string') {
        finalOptions.url = config
      } else {
        finalOptions = config
      }

      if (authHeader && !finalOptions?.headers?.Authorization) {
        finalOptions.headers = finalOptions.headers || {}
        finalOptions.headers.Authorization = authHeader
      }
      cacheKeys.push(createCacheKey(finalOptions))
      return finalOptions
    })

  let location

  if (process.env['NODE_ENV'] !== 'production') {
    const error = new Error()
    if (error.stack) {
      const split = error.stack.split('\n')
      const thisIndex = split.findIndex((s) => s.includes('useAgent'))
      try {
        location = split[thisIndex + 1].trim()
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }
  }

  function getInitialDataState(prevState = undefined): ResponseDataType {
    const initialDataState = configList.map((options, i) => {
      if (shouldCache && cache[cacheKeys[i]]) {
        return cache[cacheKeys[i]]
      }

      const hasPrevData = prevState && prevState.data

      if (isMultiReq) {
        if (hasPrevData) {
          return prevState.data[i]
        } else {
          return initialData[i]
        }
      }

      if (hasPrevData) {
        return prevState.data
      }

      return initialData
    })

    return isMultiReq ? initialDataState : initialDataState[0]
  }

  function setVal<T>(
    val
  ): typeof requestConfig extends Array<UseAgentRequestEntry> ? T[] : T {
    return Array.isArray(requestConfig) ? configList.map(() => val) : val
  }

  const [requestState, setRequestState] = useState<
    UseAgentState<ResponseDataType>
  >(
    skipRequest
      ? {
          data: getInitialDataState(),
          status: setVal(''),
          loading: setVal(false),
          success: setVal(true),
          error: setVal(undefined),
          fetchSuccessCount: 0,
          cancelTokenSource: setVal(undefined),
          hasError: getHasError(undefined),
          isLoading: getIsLoading(false),
          doFetch: fetchData
        }
      : {
          data: getInitialDataState(),
          status: setVal(''),
          loading: setVal(
            shouldCache
              ? configList.some((o, i) => !cache[cacheKeys[i]])
              : !lazy
          ),
          success: setVal(undefined),
          error: setVal(undefined),
          fetchSuccessCount: 0,
          cancelTokenSource: setVal(undefined),
          hasError: getHasError(undefined),
          isLoading: getIsLoading(
            shouldCache
              ? configList.some((o, i) => !cache[cacheKeys[i]])
              : !lazy
          ),
          doFetch: fetchData
        }
  )

  async function fetchData() {
    if (process.env['NODE_ENV'] === 'test') {
      return
    }

    // immediately bail if no longer mounted
    if (!isMounted.current || skipRequest) {
      return
    }

    const cancelTokenSources = configList.map<CancelTokenSource>(() =>
      CancelToken.source()
    ) as MaybeArrayValue<CancelTokenSource, ResponseDataType>

    const initialIsLoading = shouldCache
      ? configList.some((o, i) => !cache[cacheKeys[i]])
      : true

    setRequestState((prev) => ({
      ...prev,
      data: getInitialDataState(prev),
      loading: setVal(initialIsLoading),
      cancelTokenSource: cancelTokenSources,
      hasError: getHasError(prev.error),
      isLoading: getIsLoading(initialIsLoading)
    }))

    const errors: UseAgentError[] = configList.map(() => undefined)

    try {
      const requests: AxiosPromise<ResponseData>[] = configList.map(
        (options, i) => {
          // also check to see if we unmounted between requests
          if (!isMounted.current) {
            return
          }

          const key = cacheKeys[i]
          // Use outstanding request if available
          if (openRequests[key]) {
            return openRequests[key]
          }

          const req: Promise<any | AxiosResponse<unknown>> = apiAgent({
            ...options,
            ...(!options.cancelToken && {
              cancelToken: cancelTokenSources[i].token
            })
          })
            .then((res) => {
              const { data } = res
              // set the response data cache
              cache[key] = data
              // clear request from openRequest cache
              delete openRequests[key]

              return res
            })
            .catch((err) => {
              delete openRequests[key]
              if (axios.isCancel(err)) return
              errors[i] = err
              throw err
            })

          // add the request promise to the openRequest cache
          if ('then' in req) {
            openRequests[key] = req
          }
          return req
        }
      )

      const res = await Promise.all(requests)

      // check mount state again after all requests have settled
      // (this is the most common point where it raised errors)
      if (!isMounted.current) {
        return
      }

      const responseData = (
        isMultiReq ? res.map((r) => r.data) : res[0].data
      ) as ResponseDataType

      setRequestState((prev) => ({
        data: responseData,
        status: (Array.isArray(requestConfig)
          ? res.map((r) => r.status)
          : res[0].status) as MaybeArrayValue<number, ResponseDataType>,
        loading: setVal(false),
        success: setVal(true),
        fetchSuccessCount: prev.fetchSuccessCount + 1,
        error: (Array.isArray(requestConfig)
          ? errors
          : errors[0]) as MaybeArrayValue<UseAgentError, ResponseDataType>,
        cancelTokenSource: setVal(undefined),
        hasError: getHasError(...errors),
        isLoading: false,
        doFetch: fetchData
      }))
    } catch (err) {
      if (axios.isCancel(err)) return

      // check mount state if we caught an error too
      if (!isMounted.current) {
        return
      }

      const shouldIgnoreError =
        err.isAxiosError && (err?.response?.status ?? 0) === ignoreStatusCode

      // if ingoring error set to false, otherwise use errors array or the caught err itself
      const errorState = (
        shouldIgnoreError
          ? setVal(false)
          : getHasError(errors)
          ? errors
          : setVal(err)
      ) as MaybeArrayValue<UseAgentError, ResponseDataType>

      setRequestState((prev) => ({
        data: getInitialDataState(prev),
        status: setVal(''),
        loading: setVal(false),
        success: setVal(false),
        error: errorState,
        fetchSuccessCount: prev.fetchSuccessCount,
        cancelTokenSource: setVal(undefined),
        hasError: getHasError(errorState),
        isLoading: getIsLoading(false),
        doFetch: fetchData
      }))

      if (location) {
        // eslint-disable-next-line no-console
        console.warn(`useAgent request failed ${location}`)
      }

      // throw err
    }
  }

  useEffect(() => {
    // do not update the request state if we need to skip this request
    // If we prev had an interval set but its now disabled do not fetch again

    if (!interval && intervalRef.current) {
      // If we prev had an interval set but its now disabled do not fetch again
      clearInterval(intervalRef.current)
      intervalRef.current = undefined
    }

    if (!lazy) {
      // cannot do async/await in hook
      fetchData()
    }

    if (Number.isFinite(interval) && interval > 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      intervalRef.current = setInterval(fetchData, interval as number)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }

      // abort request on unmount
      if (!requestState.cancelTokenSource) return
      return Array.isArray(requestState.cancelTokenSource)
        ? requestState.cancelTokenSource.forEach(
            (source) => source && source.cancel()
          )
        : requestState.cancelTokenSource[0] &&
            requestState.cancelTokenSource[0].cancel()
    }
  }, [currentTeamId, lazy, shouldCache, skipRequest, interval, ...cacheKeys])

  return requestState
}
